import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import _isEmpty from 'lodash-es/isEmpty';
import _differenceWith from 'lodash-es/differenceWith';
import _isEqual from 'lodash-es/isEqual';
import Button from 'sushiweb/Button';
import Layout from 'sushiweb/Layout';
import MenuItem from 'sushiweb/MenuItem';
import grey from 'sushiweb/tokens/color/grey';
import white from 'sushiweb/tokens/color/white';
import CrossCircleFill from 'sushiweb/Icons/all/CrossCircleFill';
import { P } from 'sushiweb/Typography/Text';
import Check from 'sushiweb/Icons/all/Check';
import PlusCircle from 'sushiweb/Icons/all/PlusCircle';
import Checkbox from 'sushiweb/InputTypes/Checkbox';
import Switch from 'sushiweb/Switch';
import Dropdown from 'sushiweb/Dropdown/Dropdown';

import { DAILY, MONTHLY, WEEKLY } from '../../../common/Components/constants';

import {
  InputWrapper,
  StyledInput,
  StyledLayout,
  StyledAddSlotsDropdown,
  CustomDropdown,
  StyledChevronDown,
  StyledChevronUp,
  DropdownContainer,
  DropdownOptions,
  CheckedBox,
  UncheckedBox,
  NeverEnd,
  WarningContainer,
  ErrorContainer,
  CheckboxWrapper,
  CrossDiv,
  TopupType,
  HelperTextWrapper,
  SwitchWrapper,
  DailyRestrictions,
  ApplyLimitsWrapper,
  DropDownTextWrapper,
  StyledInputV2,
  DropDownHeaderTextWrapper,
  QuestionsHelperTextWrapper,
} from '../../../../../styles';
import {
  days,
  infoLabels,
  programStartDateError,
  programEndDateError,
  enumSlots,
  frequencyTypes,
  slotType,
  // slotStartTimeError,
  // slotEndTimeError,
  slotStartTimeGreaterThanEndTime,
} from '../../../../../constants';

// new imports
import Card from '../../../../../../common/components/Card';
import { FormCacheContext } from '../../../common/Components/FormTemplate/FormTemplate';
import {
  // constants
  minStartDate,
  minEndDate,

  // input handlers
  handleProgramNameChange,
  handleMonthlyAmountChange,
  handleStartDateChange,
  handleStartTimeChange,
  handleOpenDaysClose,
  BottomScroll,
  getVoucherProgramBody,
  handleEndDateChange,
  handleSlotNameChange,
  handleEndTimeChange,
  handleSlotAmountChange,
  handleNeverEndCheck,
  handleTopupTypeCheck,
  maxStartDate,
  handleWeeklyAmountChange,
  handleRestrictionTypeCheck,
  handleDailyAmountChange,
  validateCycleTypeAmount,
} from '../../../common/helpers/inputHandlers';

import SelectItems from '../../../../../../common/components/SelectItems';
import { genericErrorMsg } from '../../../../../../../constants';
import {
  arrayToObject,
  checkAmountInEachSlotWithBudget,
  formatDate,
  formatPOIForProgram,
  getFormattedEndDate,
  getSlotDetails,
  // getTimeIn12HRFormat,
  // slotTimeValidation,
} from '../../../../../../../helpers';
import { getAllPOI } from '../../../../../../../api/companyProfile';
import {
  applicableFlowTypes,
  currentFlowTypeEnum,
  questionStatusEnum,
  questionTypeEnum,
  statusLabels,
} from '../../../../../../common/components/constants';
import { SingleLineArrow } from '../../../../../../../components/SVGs/commonSVGs';

const VoucherProgram = ({
  flowType,
  planData,
  planDataFlag,
  selectedWorkspace,
  groupsData,
  showErrorToast,
  reviewProgram,
  reviewingProgram,
  reviewProgramDetails,
  fetchingProgramInfo,
  fetchedProgramInfo,
  programInfo,
  getProgramInfo,
  questionsData,
}) => {
  const {
    bodyStyle,
    greyBodyStyle,
    footerStyle,
    stepsVisibility,
    goToNextStep,
    getCachedFormData,
    updateCachedFormData,
  } = useContext(FormCacheContext);
  const [isCycleTypeSelected, setIsCycleTypeSelected] = useState(planDataFlag);
  const [flag, setFlag] = useState(false);
  const [errorValidation, setErrorValidation] = useState([]);
  const [locationsData, setLocationsData] = useState([]);
  const [disabled, setDisabled] = useState(true);
  const [minEnd, setMinEnd] = useState(minEndDate);
  const { groups = [] } = groupsData;

  const getPlanBudgetAmount = planDataInfo => {
    if (frequencyTypes[planDataInfo.cycle_type] === MONTHLY) {
      return planDataInfo.monthly_amount;
    }
    if (frequencyTypes[planDataInfo.cycle_type] === WEEKLY) {
      return planDataInfo.weekly_amount;
    }
    if (frequencyTypes[planDataInfo.cycle_type] === DAILY) {
      return planDataInfo.daily_amount;
    }
    return 0;
  };

  const { allGroups, selectedGroups, unSelectedGroups } = (() => {
    const formattedGroups = [];
    groups.forEach(group => {
      if (group.can_plan_be_mapped && group.active_employees > 0) {
        let groupName = group.group_name;
        if (groupName.length >= 15) {
          groupName = `${groupName.substring(0, 15)}...`;
        }
        formattedGroups.push({
          label: `${groupName} (${group.active_employees} ${
            group.active_employees > 1 ? ' employees' : ' employee'
          })`,
          value: group.group_id,
        });
      }
    });

    // getting selected groups from cache
    const { selectedGroups: sGroups = [] } = getCachedFormData();

    // getting selected groups from planData
    const planDataSGroups = planData?.groups_data?.map(group => {
      return {
        label: `${group.group_name} (${group.active_employees} ${
          group.active_employees > 1 ? ' employees' : ' employee'
        })`,
        value: group.group_id,
      };
    });

    const uGroups = _differenceWith(formattedGroups, sGroups, _isEqual);
    const planDataUGroups = _differenceWith(
      formattedGroups,
      planDataSGroups,
      _isEqual,
    );

    return {
      allGroups: formattedGroups,
      selectedGroups: planDataSGroups ?? sGroups,
      unSelectedGroups: planDataUGroups ?? uGroups,
    };
  })();

  const { allQuestions, selectedQuestions, unSelectedQuestions } = (() => {
    const formattedQuestions = [];
    questionsData.forEach(question => {
      if (question.question_status === questionStatusEnum.ACTIVE)
        formattedQuestions.push({
          label: `${question.question_definition} (${
            statusLabels[questionTypeEnum[question.question_type]]
          })`,
          value: question.question_id,
        });
    });

    // getting selected groups from cache
    const { selectedQuestions: sQuestions = [] } = getCachedFormData();

    // getting selected groups from planData
    const planDataSQuestions = planData?.questions_details?.map(question => {
      return {
        label: question.question_definition,
        value: question.question_id,
      };
    });

    const uQuestions = _differenceWith(
      formattedQuestions,
      sQuestions,
      _isEqual,
    );
    const planDataUQuestions = _differenceWith(
      formattedQuestions,
      planDataSQuestions,
      _isEqual,
    );

    return {
      allQuestions: formattedQuestions,
      selectedQuestions: planDataSQuestions ?? sQuestions,
      unSelectedQuestions: planDataUQuestions ?? uQuestions,
    };
  })();

  useEffect(() => {
    if (!(selectedLocations.length || planDataFlag)) {
      populateCompanyDetails();
    }
    stepsVisibility(false);
  }, []);

  const populateCompanyDetails = () => {
    getAllPOI(selectedWorkspace.workspace_id)
      .then(res => {
        if (!res.status) {
          showErrorToast('Unable to get location details');
          return;
        }
        const { data: offices = [] } = res;
        setLocationsData(offices);
      })
      .catch(err => {
        console.error(err);
        showErrorToast(genericErrorMsg);
      });
  };

  const { allLocations, selectedLocations, unSelectedLocations } = (() => {
    const formattedLocations = locationsData.map(location => {
      return {
        label: `${formatPOIForProgram(location.address)} (${location.poi_id})`,
        value: location.poi_id,
      };
    });

    // getting selected locations from cache
    const { selectedLocations: sLocations = [] } = getCachedFormData();

    // getting selected locations from planData
    const planDataSLocations = planData?.restrictions?.pois.map(poi => {
      return {
        label: `${poi.address} (${poi.poi_id})`,
        value: poi.poi_id,
        address: poi.address,
      };
    });

    const uLocations = _differenceWith(
      formattedLocations,
      sLocations,
      _isEqual,
    );
    const planDataULocations = _differenceWith(
      formattedLocations,
      planDataSLocations,
      _isEqual,
    );

    return {
      allLocations: formattedLocations,
      selectedLocations: planDataSLocations ?? sLocations,
      unSelectedLocations: planDataULocations ?? uLocations,
    };
  })();

  const isLimitsChecked =
    planData?.restrictions?.pois.length > 0 ||
    planData?.restrictions?.days.length < 7 ||
    (planData?.restrictions?.slots.length > 0 &&
      planData?.restrictions?.slots[0].type !== enumSlots.FullDay.type);

  // FORM DATA
  const [formData, setFormData] = useState({
    programId: planData?.program_id ?? '',
    selectedGroups: [...selectedGroups],
    unSelectedGroups: [...unSelectedGroups],
    selectedLocations: [...selectedLocations],
    unSelectedLocations: [...unSelectedLocations],
    selectedQuestions: [...selectedQuestions],
    unSelectedQuestions: [...unSelectedQuestions],
    showError: isLimitsChecked,
    name: planData?.name ?? '',
    nameErrMsg: '',
    isLimits: isLimitsChecked,
    slots: getSlotDetails(planData?.restrictions?.slots ?? []),
    slotGroups: Object.entries(enumSlots).map(value => ({
      ...value[1],
      visible: true,
    })),
    nameHtextVis: true,
    type: 1,
    workspaceID: selectedWorkspace?.workspace_id,
    monthlyAmount: planData?.monthly_amount ?? '',
    startDate: planData?.start_date ?? minStartDate,
    endDate: getFormattedEndDate(planData?.end_date) ?? '',
    openDays: false,
    selectedDays: planData?.restrictions?.days ?? [1, 2, 3, 4, 5, 6, 7],
    weekDayIds: arrayToObject(days, 'value'),
    groupIds: arrayToObject(groups, 'group_id'),
    neverEnd: planData?.never_ends ?? true,
    budget: getPlanBudgetAmount(planData),
    topupType: planData?.topup_type ?? 2,
    weeklyAmount: planData.weekly_amount ?? '',
    dailyAmount: planData.daily_amount ?? '',
    cycleType: frequencyTypes[planData.cycle_type] ?? MONTHLY,
    restrictionsType: planData.restriction_type ?? 2,
    flowType,
  });

  const updateFormData = newData => {
    setFormData(oldData => ({
      ...oldData,
      ...newData,
    }));
  };
  const getDateFromString = str => {
    const date = str.split(' ')[0];
    return date;
  };

  useEffect(() => {
    const sDate = new Date(`${getDateFromString(formData.startDate)}`);
    const eDate = formatDate(
      new Date(sDate.getFullYear(), sDate.getMonth(), sDate.getDate() + 1),
    );
    setMinEnd(eDate);
  }, [formData.startDate]);

  useEffect(() => {
    const { programId = '' } = getCachedFormData();
    if (programId && programId !== '')
      getProgramInfo({
        programId,
        workspaceId: selectedWorkspace?.workspace_id,
      });
  }, [reviewProgramDetails]);

  useEffect(() => {
    const { programId = '' } = getCachedFormData();
    if (
      programId &&
      programId !== '' &&
      !fetchingProgramInfo &&
      fetchedProgramInfo
    ) {
      if (!planDataFlag) {
        const isLimitsApplied =
          programInfo?.restrictions?.slots.length !== 0 ||
          programInfo?.restrictions?.days.length !== 0 ||
          programInfo?.restrictions?.pois.length !== 0;
        updateFormData({
          endDate: getFormattedEndDate(programInfo.end_date),
          monthlyAmount: programInfo.monthly_amount,
          name: programInfo.name,
          neverEnd: programInfo.never_ends,
          programId,
          startDate: formatDate(programInfo.start_date),
          type: 1,
          showError: false,
          slots: getSlotDetails(programInfo?.restrictions?.slots ?? []),
          slotGroups: Object.entries(enumSlots).map(value => ({
            ...value[1],
            visible: true,
          })),
          openDays: false,
          selectedDays: programInfo?.restrictions?.days ?? [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
          ],
          weekDayIds: arrayToObject(days, 'value'),
          groupIds: arrayToObject(groups, 'group_id'),
          selectedGroups: [...selectedGroups],
          unSelectedGroups: [...unSelectedGroups],
          selectedLocations: [...selectedLocations],
          unSelectedLocations: [...unSelectedLocations],
          selectedQuestions: [...selectedQuestions],
          unSelectedQuestions: [...unSelectedQuestions],
          nameHtextVis: true,
          nameErrMsg: '',
          isLimits: isLimitsApplied,
          budget: getPlanBudgetAmount(programInfo),
          topupType: programInfo?.topup_type ?? 2,
          weeklyAmount: programInfo.weekly_amount ?? '',
          dailyAmount: programInfo.daily_amount ?? '',
          cycleType: frequencyTypes[programInfo.cycle_type] ?? MONTHLY,
          restrictionsType: programInfo.restriction_type ?? 2,
          flowType,
        });
        setIsCycleTypeSelected(true);
      }
    }
  }, [fetchingProgramInfo]);

  useEffect(() => {
    const {
      validation_errors: validationErrors = [],
      program_id: programId,
    } = reviewProgramDetails;
    updateFormData({ showError: true });
    updateCachedFormData({ ...formData, programId });

    if (flag && !_isEmpty(reviewProgramDetails)) {
      setFlag(false);

      if (validationErrors?.length > 0) {
        updateFormData({ showError: true });
        setErrorValidation(validationErrors);
      } else {
        updateCachedFormData({ ...formData, programId });
        goToNextStep();
      }
    }
  }, [reviewProgramDetails]);

  const updateSelectedGroups = newGroups => {
    updateFormData({
      selectedGroups: newGroups,
      showError: false,
    });
  };
  const updateUnSelectedGroups = newGroups => {
    updateFormData({
      unSelectedGroups: newGroups,
    });
  };

  const updateSelectedLocations = newLocations => {
    updateFormData({
      selectedLocations: newLocations,
      showError: false,
    });
  };
  const updateUnSelectedLocations = newLocations => {
    updateFormData({
      unSelectedLocations: newLocations,
    });
  };
  const updateSelectedQuestion = newQustion => {
    updateFormData({
      selectedQuestions: newQustion,
      showError: false,
    });
  };
  const updateUnSelectedQuestion = newQustion => {
    updateFormData({
      unSelectedQuestions: newQustion,
    });
  };

  const handleDeleteSlots = index => () => {
    const updatedSlots = [...formData.slots];
    const updatedSlotGroups = [...formData.slotGroups];
    updatedSlots.splice(index, 1);
    if (Object.keys(updatedSlots)?.length === 0) {
      updatedSlots.push(enumSlots.FullDay);
    }
    const indexFind = updatedSlotGroups.findIndex(
      x => x.type === formData.slots[index].type,
    );
    updatedSlotGroups[indexFind].visible = true;
    updateFormData({
      slotGroups: updatedSlotGroups,
      slots: updatedSlots,
    });
  };

  const handleAddSlot = () => value => {
    if (
      Object.keys(formData.slots)?.length === 1 &&
      formData.slots[0].type === enumSlots.FullDay.type
    ) {
      formData.slots.splice(0, 1);
    }
    addSlots(value);
    const updatedSlotGroups = [...formData.slotGroups];
    updatedSlotGroups[updatedSlotGroups.indexOf(value)].visible = false;
    updateFormData({ slotGroups: updatedSlotGroups });
  };

  const handleCycleTypeSelect = val => {
    if (val === WEEKLY) {
      updateFormData({
        monthlyAmount: '',
        dailyAmount: '',
        budget: formData.weeklyAmount,
      });
    } else if (val === MONTHLY) {
      updateFormData({
        weeklyAmount: '',
        dailyAmount: '',
        budget: formData.monthlyAmount,
      });
    } else if (val === DAILY) {
      updateFormData({
        weeklyAmount: '',
        monthlyAmount: '',
        budget: formData.dailyAmount,
      });
    }
    setIsCycleTypeSelected(true);
    updateFormData({
      cycleType: val,
    });
  };

  const slotTimeValidationMsg = slot => {
    // if (slotTime < enumSlots[slotType[slot.type]].startTime) {
    //   return `${slotStartTimeError} ${getTimeIn12HRFormat(
    //     enumSlots[slotType[slot.type]].startTime,
    //   )}`;
    // }
    // if (slotTime > enumSlots[slotType[slot.type]].endTime) {
    //   return `${slotEndTimeError} ${getTimeIn12HRFormat(
    //     enumSlots[slotType[slot.type]].endTime,
    //   )}`;
    // }
    if (slot.startTime > slot.endTime) {
      return slotStartTimeGreaterThanEndTime;
    }
    return null;
  };

  useEffect(() => {
    if (
      !!formData.name &&
      !!formData.startDate &&
      !!(formData.neverEnd || formData.endDate) &&
      !!formData.topupType &&
      !!formData.selectedGroups?.length &&
      validateCycleTypeAmount(formData) &&
      !!formData.selectedDays?.length &&
      !!(
        formData.restrictionsType === 2 ||
        checkAmountInEachSlotWithBudget(formData.slots, formData.budget)
      ) &&
      // !!(!formData.isLimits || slotTimeValidation(formData.slots)) &&
      formData.selectedQuestions.length <= 4
    ) {
      setDisabled(false);
    } else if (!disabled) setDisabled(true);
  }, [formData, formData.isLimits, formData.cycleType]);

  const handleNextBtnClick = () => {
    const dateCheckFlag = dateCheck();

    if (dateCheckFlag) {
      // caches the data
      updateCachedFormData(formData);
      setFlag(true);

      const { programId } = getCachedFormData();

      let programBody = {
        programId: programId ?? '',
        name: formData.name,
        selectedGroups: formData.selectedGroups.map(group => group.value),
        selectedLocations: formData.selectedLocations.map(locations => ({
          poi_id: locations.value,
          address: locations.address,
        })),
        selectedQuestions: formData.selectedQuestions.map(
          question => question.value,
        ),
        type: formData.type,
        isLimits: formData.isLimits,
        monthlyAmount: parseFloat(formData.monthlyAmount) ?? 0,
        workspaceID: formData.workspaceID,
        selectedDays: formData.selectedDays,
        startDate: formData.startDate,
        slotsArray: formData.slots,
        endDate: formData.endDate,
        neverEnd: formData.neverEnd,
        topupType: formData.topupType,
        cycleType: formData.cycleType,
        weeklyAmount: parseFloat(formData.weeklyAmount) ?? 0,
        dailyAmount: parseFloat(formData.dailyAmount) ?? 0,
        restrictionsType: formData.restrictionsType ?? 1,
        flowType: formData.flowType,
      };

      // entity_type is set inside getVoucherProgramBody
      programBody = getVoucherProgramBody(programBody);
      reviewProgram({ programBody });
    }
  };

  const dateCheck = () => {
    if (Date.parse(`${formData.startDate}`) < Date.parse(`${minStartDate}`)) {
      showErrorToast(programStartDateError);
      return false;
    }
    if (Date.parse(`${formData.endDate}`) < Date.parse(`${minEnd}`)) {
      showErrorToast(programEndDateError);
      return false;
    }
    return true;
  };

  const selectDay = day => {
    updateFormData({ showError: false });
    const newDays = [...formData.selectedDays];
    if (newDays.includes(day)) {
      const index = newDays.indexOf(day);
      newDays.splice(index, 1);
    } else {
      newDays.push(day);
    }
    updateFormData({ selectedDays: newDays });
  };

  // constant jsx
  const renderDays = () => {
    return days.map(day => (
      <CheckboxWrapper key={day.value} onClick={() => selectDay(day.value)}>
        {formData.selectedDays.includes(day.value) ? (
          <CheckedBox>
            <Check size={15} color={white} />
          </CheckedBox>
        ) : (
          <UncheckedBox />
        )}
        <P style={{ fontSize: '15px' }} id='unselectable'>
          {day.label}
        </P>
      </CheckboxWrapper>
    ));
  };

  // jsx
  const AmountPlan = () => {
    if (!isCycleTypeSelected) {
      return (
        <Layout.Item colD={6} colT={6} colM={6}>
          <StyledInputV2 innerWidth='100%' value='Budget/employee' disabled />
        </Layout.Item>
      );
    }
    if (formData.cycleType === MONTHLY) {
      return (
        <Layout.Item colD={6} colT={6} colM={6}>
          <StyledInputV2
            value={formData.monthlyAmount}
            innerWidth='100%'
            label='Monthly budget/employee'
            onChange={handleMonthlyAmountChange(updateFormData)}
            errorMsg={formData.amountErrMsg}
            disabled={planDataFlag}
          />
        </Layout.Item>
      );
    }
    if (formData.cycleType === WEEKLY) {
      return (
        <Layout.Item colD={6} colT={6} colM={6}>
          <StyledInputV2
            value={formData.weeklyAmount}
            innerWidth='100%'
            label='Weekly budget/employee'
            onChange={handleWeeklyAmountChange(updateFormData)}
            errorMsg={formData.amountErrMsg}
            disabled={planDataFlag}
          />
        </Layout.Item>
      );
    }
    if (formData.cycleType === DAILY) {
      return (
        <Layout.Item colD={6} colT={6} colM={6}>
          <StyledInputV2
            value={formData.dailyAmount}
            innerWidth='100%'
            label='Daily budget/employee'
            onChange={handleDailyAmountChange(updateFormData)}
            errorMsg={formData.amountErrMsg}
            disabled={planDataFlag}
          />
        </Layout.Item>
      );
    }
    return <></>;
  };

  // jsx
  const ChooseSlot = () => {
    if (formData.restrictionsType === 1)
      return (
        <>
          <Layout.Item colD={12} colT={12} colM={12}>
            {!planDataFlag && (
              <WarningContainer>
                {!formData.budget || formData.budget === ''
                  ? `Note: Please enter the budget amount`
                  : `Note: Amount in meal slots should be less than or equal to the budget i.e. ₹${
                      formData.budget
                    }`}
              </WarningContainer>
            )}
            <br />
          </Layout.Item>
          {formData.slots?.map((slot, index) => {
            return (
              <Layout>
                <Layout.Item colD={3} colT={12} colM={12}>
                  <InputWrapper>
                    <StyledInput
                      label='Slot'
                      innerWidth='97%'
                      value={slotType[slot.type]}
                      onChange={handleSlotNameChange(
                        updateFormData,
                        index,
                        formData.slots,
                      )}
                      showHelperText={false}
                      disabled
                    />
                  </InputWrapper>
                </Layout.Item>
                <Layout.Item colD={3} colT={12} colM={12}>
                  <InputWrapper>
                    <StyledInput
                      label='Start Time'
                      innerWidth='97%'
                      type='time'
                      value={slot.startTime}
                      onChange={handleStartTimeChange(
                        updateFormData,
                        index,
                        formData.slots,
                      )}
                      showHelperText
                      errorMsg={slotTimeValidationMsg(slot)}
                      // min={enumSlots[slotType[slot.type]].startTime}
                      // max={enumSlots[slotType[slot.type]].endTime}
                    />
                  </InputWrapper>
                </Layout.Item>
                <Layout.Item colD={3} colT={12} colM={12}>
                  <InputWrapper>
                    <StyledInput
                      label='End Time'
                      innerWidth='97%'
                      type='time'
                      value={slot.endTime}
                      onChange={handleEndTimeChange(
                        updateFormData,
                        index,
                        formData.slots,
                      )}
                      showHelperText
                      errorMsg={slotTimeValidationMsg(slot)}
                      // min={enumSlots[slotType[slot.type]].startTime}
                      // max={enumSlots[slotType[slot.type]].endTime}
                    />
                  </InputWrapper>
                </Layout.Item>
                <Layout.Item colD={planDataFlag ? 3 : 2.5} colT={12} colM={12}>
                  <InputWrapper>
                    <StyledInput
                      innerWidth='97%'
                      value={slot.amount}
                      label='Amount'
                      showHelperText={false}
                      onChange={handleSlotAmountChange(
                        updateFormData,
                        index,
                        formData.slots,
                      )}
                      disabled={planDataFlag}
                    />
                  </InputWrapper>
                </Layout.Item>
                {slot.type !== enumSlots.FullDay.type && !planDataFlag && (
                  <Layout.Item colD={0.5} colT={12} colM={12}>
                    <InputWrapper>
                      <CrossDiv>
                        <CrossCircleFill onClick={handleDeleteSlots(index)} />
                      </CrossDiv>
                    </InputWrapper>
                  </Layout.Item>
                )}
              </Layout>
            );
          })}
          {!planDataFlag && (
            <Layout.Item colD={12} colT={12} colM={12}>
              <StyledAddSlotsDropdown
                isNaked
                width='90%'
                staticHeader={(
                  <P color='#EF4F5F'>
                    Add new slot
                    {' '}
                    <PlusCircle
                      color='#EF4F5F'
                      style={{ paddingLeft: '8px' }}
                    />
                    {' '}
                  </P>
                )}
                onSelect={handleAddSlot()}
                isTransparent
                isCentered
                disabled={planDataFlag}
              >
                {formData.slotGroups.map(item => {
                  return (
                    <MenuItem
                      value={item}
                      label={slotType[item.type]}
                      isVisible={
                        item.type !== enumSlots.FullDay.type && item.visible
                      }
                    />
                  );
                })}
              </StyledAddSlotsDropdown>
            </Layout.Item>
          )}
        </>
      );
    return <></>;
  };

  // jsx
  const LimitsCard = () => {
    if (formData.isLimits) {
      return (
        <div>
          <Card.Body style={greyBodyStyle}>
            <StyledLayout gap={10}>
              <Layout.Item colD={12} colT={12} colM={12}>
                <span style={{ fontSize: '17.5px', color: grey.z600 }}>
                  Applicable days
                </span>
                <InputWrapper>
                  <DropdownContainer
                    handleClickOutside={handleOpenDaysClose}
                    disabled={planDataFlag}
                  >
                    <CustomDropdown
                      open={formData.openDays}
                      style={{ pointerEvents: planDataFlag ? 'none' : 'all' }}
                      onClick={() =>
                        updateFormData({ openDays: !formData.openDays })}
                      dark={formData.selectedDays.length !== 0}
                      disabled={planDataFlag}
                    >
                      {formData.selectedDays.length === 0 ? (
                        <P id='unselectable'>Select custom day(s)</P>
                      ) : (
                        <P id='unselectable'>
                          {formData.selectedDays.map((selectedDay, index) => {
                            if (index === 0)
                              return `${
                                formData.weekDayIds[selectedDay].label
                              }`;
                            return `, ${
                              formData.weekDayIds[selectedDay].label
                            }`;
                          })}
                        </P>
                      )}
                      {formData.openDays ? (
                        <StyledChevronUp size={24} color={grey.z900} />
                      ) : (
                        <StyledChevronDown size={24} color={grey.z900} />
                      )}
                    </CustomDropdown>
                    {formData.openDays && (
                      <DropdownOptions>
                        <DropdownOptions.Groups>
                          {renderDays()}
                        </DropdownOptions.Groups>
                        <DropdownOptions.ApplyFilter>
                          <P id='unselectable'>
                            {`${
                            formData.selectedDays.length
                          }/${days.length}`}
                          </P>
                          <Button
                            size='small'
                            onClick={handleOpenDaysClose(updateFormData)}
                          >
                            Apply
                          </Button>
                        </DropdownOptions.ApplyFilter>
                      </DropdownOptions>
                    )}
                  </DropdownContainer>
                </InputWrapper>
              </Layout.Item>
              <Layout.Item colD={12} colT={12} colM={12}>
                <DailyRestrictions>
                  <div>
                    <DailyRestrictions.Text>
                      Apply restriction on daily usage?
                    </DailyRestrictions.Text>
                    <DailyRestrictions.HelperText style={{ display: 'flex' }}>
                      {infoLabels.restrictionType}
                    </DailyRestrictions.HelperText>
                  </div>
                  <SwitchWrapper>
                    <Switch
                      onToggle={val => {
                        handleRestrictionTypeCheck(
                          updateFormData,
                          val,
                          formData,
                        );
                      }}
                      active={formData.restrictionsType === 1}
                      readOnly={planDataFlag}
                    />
                  </SwitchWrapper>
                </DailyRestrictions>
              </Layout.Item>
              <Layout.Item colD={12} colT={12} colM={12}>
                {ChooseSlot()}
              </Layout.Item>

              <Layout.Item colD={12} colT={12} colM={12}>
                <InputWrapper>
                  <span
                    style={{
                      fontSize: '17.5px',
                      color: grey.z600,
                      paddingTop: '10px',
                      paddingBottom: '10px',
                    }}
                  >
                    Delivery locations
                  </span>
                  <SelectItems
                    allItems={allLocations}
                    selectedItems={formData.selectedLocations}
                    unSelectedItems={formData.unSelectedLocations}
                    updateSelectedItems={updateSelectedLocations}
                    updateUnSelectedItems={updateUnSelectedLocations}
                    placeholder='Select locations'
                    disabled={planDataFlag}
                  />
                </InputWrapper>
              </Layout.Item>
            </StyledLayout>
          </Card.Body>
        </div>
      );
    }
    return <></>;
  };

  const handleRestriction = () => {
    updateFormData({
      isLimits: !formData.isLimits,
      selectedDays: [1, 2, 3, 4, 5, 6, 7],
      unSelectedLocations,
    });
    if (formData.slots.length === 0) formData.slots.push(enumSlots.FullDay);
  };

  const addSlots = value => {
    updateFormData({
      slots: [...formData.slots, value],
    });
  };

  // jsx
  return (
    <>
      <Card.Body style={bodyStyle}>
        <StyledLayout gap={20}>
          <Layout.Item colD={12} colT={12} colM={12}>
            <InputWrapper>
              <StyledInput
                value={formData.name}
                innerWidth='100%'
                label='Plan name'
                onChange={handleProgramNameChange(updateFormData)}
                errorMsg={formData.nameErrMsg}
                disabled={planDataFlag}
                style={{ fontWeight: 300 }}
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={6} colT={12} colM={12}>
            <InputWrapper>
              <StyledInput
                label='Start date'
                innerWidth='100%'
                type='date'
                value={getDateFromString(formData.startDate)}
                min={minStartDate}
                max={maxStartDate}
                onChange={handleStartDateChange(updateFormData)}
                disabled={planDataFlag}
                style={{ marginTop: '-5px', fontWeight: 300 }}
                // helperText={infoLabels.start_date}
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={6} colT={12} colM={12}>
            <InputWrapper>
              <StyledInput
                label='End date'
                innerWidth='100%'
                type='date'
                value={getDateFromString(formData.endDate)}
                min={minEnd}
                onChange={handleEndDateChange(updateFormData)}
                disabled={formData.neverEnd || planDataFlag}
                style={{ marginTop: '-5px', fontWeight: 300 }}
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={6} colT={6} colM={6}>
            <HelperTextWrapper>
              {flowType === currentFlowTypeEnum.FLOW_TYPE_LIMIT
                ? infoLabels.limitStartDate
                : infoLabels.startDate}
            </HelperTextWrapper>
          </Layout.Item>
          <Layout.Item colD={4} colT={4} colM={4}>
            <HelperTextWrapper style={{ marginLeft: '5px' }}>
              {flowType === currentFlowTypeEnum.FLOW_TYPE_LIMIT
                ? infoLabels.limitEndDate
                : infoLabels.endDate}
            </HelperTextWrapper>
          </Layout.Item>
          <Layout.Item colD={2} colT={2} colM={2}>
            <NeverEnd>
              <NeverEnd.Container
                style={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                <Checkbox
                  checked={formData.neverEnd}
                  onChange={handleNeverEndCheck(
                    updateFormData,
                    formData,
                    minEnd,
                  )}
                  label='Never end'
                  color='#9197A6'
                  disabled={planDataFlag}
                />
              </NeverEnd.Container>
            </NeverEnd>
          </Layout.Item>
          <Layout.Item colD={12} colT={12} colM={12}>
            <InputWrapper>
              <SelectItems
                allItems={allGroups}
                selectedItems={formData.selectedGroups}
                unSelectedItems={formData.unSelectedGroups}
                updateSelectedItems={updateSelectedGroups}
                updateUnSelectedItems={updateUnSelectedGroups}
                placeholder='Select team(s)'
                disabled={planDataFlag}
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={6} colT={6} colM={6}>
            {flowType !== currentFlowTypeEnum.FLOW_TYPE_LIMIT ? (
              <>
                {planDataFlag ? (
                  <StyledInputV2
                    innerWidth='100%'
                    value={formData.cycleType}
                    disabled
                  />
                ) : (
                  <Dropdown
                    value={
                      isCycleTypeSelected ? (
                        formData.cycleType
                      ) : (
                        <DropDownHeaderTextWrapper>
                          Top-up frequency
                        </DropDownHeaderTextWrapper>
                      )
                    }
                    placeholder='Top-up frequency'
                    disabled={planDataFlag}
                    onSelect={handleCycleTypeSelect}
                    width='360px'
                  >
                    <MenuItem
                      value={WEEKLY}
                      label={(
                        <DropDownTextWrapper> 
                          {' '}
                          {WEEKLY}
                          {' '}
                        </DropDownTextWrapper>
                      )}
                    />
                    <MenuItem
                      value={MONTHLY}
                      label={(
                        <DropDownTextWrapper> 
                          {' '}
                          {MONTHLY}
                          {' '}
                        </DropDownTextWrapper>
                      )}
                    />
                  </Dropdown>
                )}
              </>
            ) : (
              <>
                {planDataFlag ? (
                  <StyledInputV2
                    innerWidth='100%'
                    value={formData.cycleType}
                    disabled
                  />
                ) : (
                  <Dropdown
                    innerWidth='100%'
                    value={
                      isCycleTypeSelected ? (
                        formData.cycleType
                      ) : (
                        <DropDownHeaderTextWrapper>
                          Frequency
                        </DropDownHeaderTextWrapper>
                      )
                    }
                    placeholder='Frequency'
                    disabled={planDataFlag}
                    onSelect={handleCycleTypeSelect}
                    width='360px'
                  >
                    <MenuItem
                      value={DAILY}
                      label={(
                        <DropDownTextWrapper> 
                          {' '}
                          {DAILY}
                          {' '}
                        </DropDownTextWrapper>
                      )}
                    />
                    <MenuItem
                      value={WEEKLY}
                      label={(
                        <DropDownTextWrapper> 
                          {' '}
                          {WEEKLY}
                          {' '}
                        </DropDownTextWrapper>
                      )}
                    />
                    <MenuItem
                      value={MONTHLY}
                      label={(
                        <DropDownTextWrapper> 
                          {' '}
                          {MONTHLY}
                          {' '}
                        </DropDownTextWrapper>
                      )}
                    />
                  </Dropdown>
                )}
              </>
            )}
          </Layout.Item>

          {AmountPlan()}
          {flowType === currentFlowTypeEnum.FLOW_TYPE_LIMIT &&
            questionsData.length > 0 && (
              <>
                <Layout.Item colD={12} colT={12} colM={12}>
                  <InputWrapper>
                    <SelectItems
                      allItems={allQuestions}
                      selectedItems={formData.selectedQuestions}
                      unSelectedItems={formData.unSelectedQuestions}
                      updateSelectedItems={updateSelectedQuestion}
                      updateUnSelectedItems={updateUnSelectedQuestion}
                      placeholder='Details required from employees (Optional)'
                      disabled={planDataFlag}
                      checkLength
                      selectedItemsLength={4}
                    />
                  </InputWrapper>
                </Layout.Item>
                <Layout.Item colD={12} colT={12} colM={12}>
                  <QuestionsHelperTextWrapper>
                    Business details to be submitted by employees while placing
                    an order.
                  </QuestionsHelperTextWrapper>
                </Layout.Item>
              </>
            )}
          {flowType !== currentFlowTypeEnum.FLOW_TYPE_LIMIT && (
            <Layout.Item colD={12} colT={12} colM={12}>
              <TopupType>
                <div>
                  <TopupType.Text>
                    Allow unused budget to carry forward for the employees?
                  </TopupType.Text>
                  <TopupType.HelperText style={{ display: 'flex' }}>
                    {infoLabels.accumulating}
                  </TopupType.HelperText>
                </div>
                <SwitchWrapper>
                  <TopupType.YesText>Yes</TopupType.YesText>
                  <Switch
                    onToggle={val => {
                      handleTopupTypeCheck(updateFormData, val);
                    }}
                    active={formData.topupType === 1}
                    readOnly={planDataFlag}
                  />
                </SwitchWrapper>
              </TopupType>
            </Layout.Item>
          )}
          <Layout.Item colD={12} colT={12} colM={12}>
            <ApplyLimitsWrapper isLimitsApplied={formData.isLimits}>
              <ApplyLimitsWrapper.Container>
                <Checkbox
                  checked={formData.isLimits}
                  onChange={handleRestriction}
                  label={(
                    <span style={{ fontSize: '16px', color: grey.z600 }}>
                      Apply usage rules
                    </span>
                  )}
                  color='#9197A6'
                  disabled={planDataFlag}
                />
              </ApplyLimitsWrapper.Container>
              <ApplyLimitsWrapper.HelperText>
                Add restrictions for mealtime slots, days and/or delivery
                locations
              </ApplyLimitsWrapper.HelperText>
            </ApplyLimitsWrapper>
          </Layout.Item>
          {LimitsCard()}
        </StyledLayout>
        {formData.showError && errorValidation.length > 0 && (
          <>
            <BottomScroll />
            <ErrorContainer>
              {errorValidation.map(err => {
                return <p>{err}</p>;
              })}
            </ErrorContainer>
          </>
        )}
      </Card.Body>
      {!planDataFlag && (
        <Card.Footer style={footerStyle}>
          <Button
            onClick={handleNextBtnClick}
            loading={reviewingProgram}
            disabled={disabled}
            icon={<SingleLineArrow />}
            iconAlign='right'
          >
            Continue
          </Button>
        </Card.Footer>
      )}
    </>
  );
};

VoucherProgram.propTypes = {
  flowType: PropTypes.oneOf(applicableFlowTypes),
  planData: PropTypes.objectOf(PropTypes.any),
  planDataFlag: PropTypes.bool,
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  // group related
  groupsData: PropTypes.objectOf(PropTypes.any),
  showErrorToast: PropTypes.func,

  // program review related
  reviewProgram: PropTypes.func,
  reviewingProgram: PropTypes.bool,
  reviewProgramDetails: PropTypes.objectOf(PropTypes.any),

  programInfo: PropTypes.objectOf(PropTypes.any),
  getProgramInfo: PropTypes.func,
  fetchingProgramInfo: PropTypes.bool,
  fetchedProgramInfo: PropTypes.bool,
  questionsData: PropTypes.arrayOf(PropTypes.any),
};

VoucherProgram.defaultProps = {
  flowType: currentFlowTypeEnum.FLOW_TYPE_PPI,
  planData: {},
  planDataFlag: false,
  selectedWorkspace: {},
  // group related
  groupsData: {},
  showErrorToast: _noop,

  // program review related
  reviewProgramDetails: {},
  reviewProgram: _noop,
  reviewingProgram: false,
  programInfo: {},
  getProgramInfo: _noop,
  fetchedProgramInfo: true,
  fetchingProgramInfo: true,
  questionsData: [],
};

export default VoucherProgram;
